import { useMemo, useState } from "react";
import type { SceneCategoryDto, SceneProductDto } from "../types";

export interface CatalogPanelProps {
  categories: SceneCategoryDto[];
  products: SceneProductDto[];
  /**
   * On small screens the panel is a slide-in drawer. When the host controls open
   * state (e.g. from a hamburger button in the top bar) it passes both.
   */
  isOpen?: boolean;
  onRequestClose?: () => void;
}

interface NavCrumb {
  id: number | null; // null = root
  name: string;
}

/**
 * Root-down category browser. Click a category to drill in (breadcrumb appears),
 * click a product card to start dragging into the Unity viewport. The drag itself
 * is handled by configurator-viewer.js's DragDropManager — it watches for any
 * pointerdown on an element carrying [data-product-id], so the cards here just
 * advertise that attribute and the JS module takes it from there.
 */
export function CatalogPanel({ categories, products, isOpen = true, onRequestClose }: CatalogPanelProps) {
  const [crumbs, setCrumbs] = useState<NavCrumb[]>([{ id: null, name: "Catalog" }]);

  const productById = useMemo(() => {
    const m = new Map<string, SceneProductDto>();
    for (const p of products) m.set(p.id, p);
    return m;
  }, [products]);

  const currentCategoryId = crumbs[crumbs.length - 1].id;

  const childCategories = useMemo(
    () => categories.filter(c => c.parentId === currentCategoryId).sort((a, b) => a.sortOrder - b.sortOrder),
    [categories, currentCategoryId]
  );

  const productsHere = useMemo(() => {
    if (currentCategoryId == null) return [];
    const cat = categories.find(c => c.id === currentCategoryId);
    if (!cat) return [];
    return cat.productIds.map(pid => productById.get(pid)).filter((p): p is SceneProductDto => !!p);
  }, [categories, currentCategoryId, productById]);

  const enterCategory = (c: SceneCategoryDto) => {
    setCrumbs(prev => [...prev, { id: c.id, name: c.name }]);
  };

  const popTo = (index: number) => {
    setCrumbs(prev => prev.slice(0, index + 1));
  };

  return (
    <aside className={"sd-catalog" + (isOpen ? " sd-catalog--open" : "")}>
      <header className="sd-catalog__header">
        <nav className="sd-catalog__crumbs" aria-label="Catalog breadcrumb">
          {crumbs.map((c, i) => {
            const isLast = i === crumbs.length - 1;
            return (
              <span key={i} className="sd-catalog__crumb">
                {isLast ? (
                  <span className="sd-catalog__crumb-current">{c.name}</span>
                ) : (
                  <button type="button" className="sd-catalog__crumb-link" onClick={() => popTo(i)}>
                    {c.name}
                  </button>
                )}
                {!isLast && <span className="sd-catalog__crumb-sep">›</span>}
              </span>
            );
          })}
        </nav>
        {onRequestClose && (
          <button type="button" className="sd-catalog__close" onClick={onRequestClose} aria-label="Close catalog">
            ×
          </button>
        )}
      </header>

      <div className="sd-catalog__body">
        {childCategories.length === 0 && productsHere.length === 0 && (
          <p className="sd-catalog__empty">This category is empty.</p>
        )}

        {childCategories.length > 0 && (
          <section className="sd-catalog__section">
            <h6 className="sd-catalog__section-heading">Categories</h6>
            <ul className="sd-catalog__list">
              {childCategories.map(c => (
                <li key={c.id}>
                  <button type="button" className="sd-catalog__category" onClick={() => enterCategory(c)}>
                    {c.iconUrl ? (
                      <img src={c.iconUrl} alt="" className="sd-catalog__icon" />
                    ) : (
                      <span className="sd-catalog__icon sd-catalog__icon--folder">📁</span>
                    )}
                    <span className="sd-catalog__category-text">
                      <span className="sd-catalog__category-name">{c.name}</span>
                      {c.description && <span className="sd-catalog__category-desc">{c.description}</span>}
                    </span>
                    <span className="sd-catalog__chevron">›</span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}

        {productsHere.length > 0 && (
          <section className="sd-catalog__section">
            <h6 className="sd-catalog__section-heading">Products</h6>
            <ul className="sd-catalog__grid">
              {productsHere.map(p => (
                <li key={p.id}>
                  {/*
                    [data-product-id] is the contract with configurator-viewer.js's
                    DragDropManager — its global pointerdown listener picks up any
                    element with this attribute and starts a drag. The browser-item
                    class is what its ghost-clone code expects to find.
                  */}
                  <div className="sd-catalog__product browser-item" data-product-id={p.id} title="Drag to place in scene">
                    <div className="sd-catalog__product-img">
                      {p.imageUrl ? <img src={p.imageUrl} alt={p.name} draggable={false} /> : <span className="sd-catalog__product-placeholder">📦</span>}
                    </div>
                    <div className="sd-catalog__product-name">{p.name}</div>
                    <div className="drag-overlay">⤴ Drag to Scene</div>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </aside>
  );
}
